import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Layout, message, Spin } from 'antd';
import { useEffectWithPrev } from '@owl-frontend/components';
import type { Participant } from '../../api/claims/interface';
import { Locale, SessionContext } from '../../context/AppProvider';
import eventBusHooks, {
  type TypedEventBus,
  useBus,
} from '../../context/event-bus';
import { FeatureKeys } from '../../data/session/interface';
import useUsersHook from '../AdminPanelContainer/UsersContainer/useUsersHook';
import type { ClaimsListEvents } from './data/logic';
import useClaimsHook, { type ClaimsHookState } from './useClaimsHook';
import styles from './ClaimsContainer.module.scss';
import ClaimsListComponent from './ClaimsListComponent/ClaimsListComponent';
import ClaimsListCustomerTermComponent from './ClaimsListComponent/ClaimsListCustomerTermComponent';
import ClaimsListFilterComponent from './ClaimsListFilterComponent/ClaimsListFilterComponent';
import SelfServiceClaimCreationModalComponent from './SelfServiceClaimCreationModalComponent/SelfServiceClaimCreationModalComponent';

const events: TypedEventBus<ClaimsListEvents> = eventBusHooks;

const ClaimsContainer: React.FC = () => {
  const bus = useBus<ClaimsListEvents>();
  const { messages } = React.useContext(Locale);
  const { features } = React.useContext(SessionContext);
  const [createModal, setCreateModal] = React.useState(false);
  const [selectedCase, setSelectedCase] = React.useState<{
    firstName?: string;
    lastName?: string;
    claimants?: Participant[];
    clientDossierId: string;
    notes: string;
    dossierId: string;
    data: {
      claimants: string[];
      insured: string;
    };
    dossierType: string;
  }>();
  const [messageApi, contextHolder] = message.useMessage();
  const [
    {
      claims,
      filters,
      sort,
      pagination,
      actions: {
        deleteClaim,
        fetchClaims,
        createSelfServiceClaim,
        updateClaimFiles,
      },
    },
    handler,
  ] = useClaimsHook();

  const [
    {
      users,
      actions: { users: fetchUsers },
    },
    usersHookHandler,
  ] = useUsersHook();

  events.useListener(
    'invalidateClaimsPage',
    React.useCallback(() => {
      handler.fetchClaims({
        filters,
        sort,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
      });
    }, [filters, handler, pagination.currentPage, pagination.pageSize, sort])
  );

  const onPageChange = React.useCallback(
    (data: {
      currentPage: number;
      pageSize: number;
      total: number;
      sort: any;
    }) => {
      handler.onPageChange(data);
    },
    [handler]
  );

  const fetchClaimsDebounced = useDebouncedCallback(
    (data: {
      filters: ClaimsHookState['filters'];
      sort: ClaimsHookState['sort'];
      currentPage: number;
      pageSize: number;
    }) => {
      handler.fetchClaims({
        filters: data.filters,
        sort: data.sort,
        currentPage: data.currentPage,
        pageSize: data.pageSize,
      });
    },
    5_00, // 500 ms
    {
      leading: false,
      trailing: true,
      maxWait: 1_000,
    }
  );

  const onClearFilter = React.useCallback(() => {
    handler.onFilterChange({ filters: {} });
  }, [handler]);

  React.useEffect(() => {
    fetchClaimsDebounced({
      filters,
      sort,
      currentPage: pagination.currentPage,
      pageSize: pagination.pageSize,
    });
  }, [
    filters,
    sort,
    pagination.currentPage,
    pagination.pageSize,
    fetchClaimsDebounced,
  ]);

  React.useEffect(() => {
    handler.onPageChange({
      ...pagination,
      currentPage: 1,
    });
    // eslint-disable-next-line
  }, [handler, filters]);

  React.useEffect(() => {
    usersHookHandler.fetchUsers();
  }, [usersHookHandler]);

  React.useEffect(() => {
    handler.pollClaims(bus);
  }, [handler, bus]);

  React.useEffect(
    () => () => {
      handler.stopPollingClaims();
    },
    [handler]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (createSelfServiceClaim.status === 'fulfilled') {
        messageApi.success(
          messages['claims.hub.self_service.create_modal.success']
        );

        handler.fetchClaims({
          filters,
          sort,
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
        });
      }

      if (createSelfServiceClaim.status === 'rejected') {
        messageApi.error(
          messages['claims.hub.self_service.create_modal.error']
        );
      }
    },
    [
      createSelfServiceClaim.status,
      filters,
      sort,
      handler,
      messages,
      messageApi,
      features,
      pagination.currentPage,
      pagination.pageSize,
    ]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (updateClaimFiles.status === 'fulfilled') {
        messageApi.success(
          messages['claims.hub.self_service.create_modal.update_success']
        );

        handler.fetchClaims({
          filters,
          sort,
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
        });
      }

      if (updateClaimFiles.status === 'rejected') {
        messageApi.error(
          messages['claims.hub.self_service.create_modal.update_error']
        );
      }
    },
    [
      updateClaimFiles.status,
      messages,
      messageApi,
      handler,
      filters,
      sort,
      pagination.currentPage,
      pagination.pageSize,
    ]
  );

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      handler.fetchClaims({
        filters,
        sort,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
      });
    },
    [
      deleteClaim.status,
      features,
      filters,
      handler,
      pagination.currentPage,
      pagination.pageSize,
      sort,
    ]
  );

  if (fetchUsers.status === 'pending') {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout className={styles.content}>
      {features[FeatureKeys.SelfServiceView] && (
        <div className={styles.topPanelContainer}>
          <Button
            onClick={() => {
              setCreateModal(true);
              setSelectedCase(undefined);
            }}
          >
            <PlusOutlined /> {messages['claims.hub.self_service.title']}
          </Button>
        </div>
      )}
      {contextHolder}
      <Spin spinning={fetchClaims.status === 'pending'}>
        <ClaimsListFilterComponent
          users={users}
          filters={filters}
          onFilterChange={handler.onFilterChange}
          advancedFilter={features[FeatureKeys.CustomerTerminology]}
          onClearFilter={onClearFilter}
        />
      </Spin>

      <div className={styles.tableContainer}>
        {!features[FeatureKeys.CustomerTerminology] ? (
          <ClaimsListComponent
            data={claims}
            loading={claims.length === 0 && fetchClaims.status === 'pending'}
            onSortChange={handler.onSortChange}
            onPageChange={onPageChange}
            pagination={pagination}
            setCreateModal={setCreateModal}
            setSelectedCase={setSelectedCase}
            onClearFilter={onClearFilter}
          />
        ) : (
          <ClaimsListCustomerTermComponent
            data={claims}
            loading={claims.length === 0 && fetchClaims.status === 'pending'}
            onSortChange={handler.onSortChange}
            onPageChange={onPageChange}
            pagination={pagination}
            onClearFilter={onClearFilter}
          />
        )}
      </div>

      {features[FeatureKeys.SelfServiceView] && (
        <SelfServiceClaimCreationModalComponent
          loading={
            createSelfServiceClaim.status === 'pending' ||
            updateClaimFiles.status === 'pending'
          }
          visible={createModal}
          onSubmit={handler.createSelfServiceClaim}
          onUpdate={handler.updateClaimFiles}
          onClose={() => {
            setCreateModal(false);
          }}
          initialValues={selectedCase}
        />
      )}
    </Layout>
  );
};

export default ClaimsContainer;
